<script setup lang="ts">
import type { Media } from "@/models"
import type { ITagRequest } from "@/types"
import { useMutation } from "@tanstack/vue-query"
import { UseFocusTrap } from "@vueuse/integrations/useFocusTrap/component"
import { computed } from "vue"
import { addTags, deleteTags } from "@/api"
import { MediaTile } from "@/components"
import { BaseModal, Button, Card, Icon, TagInput } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { MediaType } from "@/models"
import { MediaTagsModalKey } from "@/symbols"
import { ContentType } from "@/types"

interface MediaTagsModalProps {
  eventVaultId?: number
  media: Media[]
  eventTags: Array<string>
  isLoading?: boolean
}

const props = defineProps<MediaTagsModalProps>()

const emit = defineEmits<{
  (e: "success"): void
  (e: "close"): void
  (e: "error"): void
}>()

const { t } = useTranslation()
const isSingleMedia = computed(() => props.media.length === 1)
const mediaTags = computed(() => {
  if (isSingleMedia.value)
    return props.media[0]?.tags ? [...props.media[0].tags] : []
  return []
})

const { toggle, isActive } = useModal(MediaTagsModalKey)
function onToggle() {
  if (isActive?.value)
    emit("close")
  toggle()
}

const { mutate: addMutate, isPending: isAddPending } = useMutation({
  mutationKey: ["addTags", props.eventVaultId, props.media],
  mutationFn: (tags: ITagRequest) => addTags(tags, props.eventVaultId),
  onSuccess: async () => {
    emit("success")
  },
  onError: async () => {
    emit("error")
    onToggle()
  },
})

const { mutate: deleteMutate, isPending: isPendingDelete } = useMutation({
  mutationKey: ["deleteTags", props.eventVaultId, props.media],
  mutationFn: (tags: ITagRequest) => deleteTags(tags, props.eventVaultId),
  onSuccess: async () => {
    emit("success")
  },
  onError: async () => {
    emit("error")
    onToggle()
  },
})

function executeTagMutation(singleTag: string[], mutateAction: (params: ITagRequest) => void) {
  props.media.forEach((media) => {
    mutateAction({
      contentId: media.id!,
      contentType: media.type === MediaType.Vid ? ContentType.Video : ContentType.Photo,
      tags: singleTag,
    })
  })
}

const isLoading = computed(() => props.isLoading || isAddPending.value || isPendingDelete.value)
</script>

<template>
  <BaseModal :name="MediaTagsModalKey">
    <UseFocusTrap>
      <Card>
        <div class="flex justify-between items-center mb-3">
          <h2 class="text-2xl text-nowrap m-0">
            {{ isSingleMedia ? t("media_tags_modal.single_title") : t("media_tags_modal.multiple_title") }}
          </h2>
          <Button variant="icon" @click="onToggle">
            <span class="sr-only">{{ t("media_tags_modal.close_sr") }}</span>
            <Icon icon="Close" size="sm" />
          </Button>
        </div>

        <div v-if="media" class="grid gap-3 grid-cols-1 mt-6 mb-3 md:grid-cols-2">
          <MediaTile v-for="(item, index) in media.slice(0, 3)" :key="`media-${index}`" :media="item" />
          <div v-if="media.slice(3).length" class="flex gap-4 border border-gray-300 p-3 shadow-lg rounded-sm">
            <div>
              <h5 class="font-light text-lg text-gray-800 m-0 mb-1">
                {{ t("media_tags_modal.multiple_count", { count: media.slice(3).length }) }}
              </h5>
            </div>
          </div>
        </div>

        <TagInput
          class="mb-3"
          label="Tags"
          :model-value="mediaTags"
          :existing-tags="eventTags"
          :is-loading="isLoading"
          :hint="t('media_tags_modal.taginput_hint')"
          @add-tag="(tag) => executeTagMutation([tag], addMutate)"
          @delete-tag="(tag) => executeTagMutation([tag], deleteMutate)"
        />
      </Card>
    </UseFocusTrap>
  </BaseModal>
</template>
